import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { Params, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of, interval } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { trigger, transition, style, animate } from '@angular/animations';
import { CorporateFormsService } from '../../corporate/services/corporate-forms.service';
import { GlobalRequestDialogService } from '../../global-requests/services/global-request-dialog.service';
import { UserService } from '../../services/user.service';
import { CompaniesService } from '../../services/companies.service';
import { PermissionsService } from '../../services/permissions.service';
import { ConfirmDialogComponent } from '../../corporate/confirm-dialog/confirm-dialog.component';
import {
  NotificationVM,
  NotificationType,
  enrichNotification,
  getNotificationAvatar,
  getStatusTag,
  isPendingChangeNotification,
  taskListLink,
} from '../models/notification.model';
import { offerOtherOrgTab, openInOrgTab } from '../../shared/navigation/org-tab';

interface FilterOption {
  label: string;
  value: string;
}

@Component({
    selector: 'app-notifications-page',
    templateUrl: './notifications-page.component.html',
    styleUrls: ['./notifications-page.component.scss'],
    animations: [
        trigger('cardAnimation', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(12px)' }),
                animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
            ]),
            transition(':leave', [
                animate('250ms ease-in', style({ opacity: 0, transform: 'translateX(40px)' }))
            ])
        ])
    ],
    standalone: false
})
export class NotificationsPageComponent implements OnInit {

  private readonly formsService = inject(CorporateFormsService);
  private readonly globalRequestDialog = inject(GlobalRequestDialogService);
  private readonly userService = inject(UserService);
  private readonly companiesService = inject(CompaniesService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroy = inject(DestroyRef);

  notifications: NotificationVM[] = [];
  filteredNotifications: NotificationVM[] = [];
  isLoading = false;
  isAdmin = false;
  activeFilter = 'all';
  deletingIds = new Set<number>();
  /** Companies the user belongs to, by id — "Open in <organisation>" is offered only for these. */
  private userCompanies = new Map<number, string>();

  filterOptions: FilterOption[] = [
    { label: 'All', value: 'all' },
    { label: 'Leave', value: 'leave' },
    { label: 'Approvals', value: 'approvals' },
    { label: 'System', value: 'system' }
  ];

  get unreadCount(): number {
    return this.notifications.filter(n => !n.isRead).length;
  }

  ngOnInit(): void {
    this.loadNotifications();

    this.userService.getUserCompaniesAsObs().pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(companies => this.userCompanies = new Map((companies ?? []).map(c => [c.id, c.name])));

    // Check admin status
    this.userService.getCurrentUser(false).pipe(
      catchError(() => of(null))
    ).subscribe(user => {
      if (user) {
        this.isAdmin = this.permissionsService.isCorporateAdmin();
      }
    });

    // Poll for updates every 30 seconds on the page
    interval(30000).pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(() => this.loadNotifications());
  }

  private loadNotifications(): void {
    this.formsService.getAllNotifications().pipe(
      catchError(err => {
        console.error('Error loading notifications:', err);
        return of([]);
      })
    ).subscribe(notes => {
      this.notifications = notes.map(n => enrichNotification(n));
      this.applyFilter();
    });
  }

  setFilter(value: string): void {
    this.activeFilter = value;
    this.applyFilter();
  }

  private applyFilter(): void {
    if (this.activeFilter === 'all') {
      this.filteredNotifications = [...this.notifications];
      return;
    }

    const typeMap: Record<string, NotificationType[]> = {
      'leave': [NotificationType.LeaveRequest, NotificationType.LeaveApproved, NotificationType.LeaveRejected],
      'meetings': [NotificationType.MeetingMention, NotificationType.Comment],
      'approvals': [NotificationType.Approval],
      'system': [NotificationType.System, NotificationType.Reaction]
    };

    const allowedTypes = typeMap[this.activeFilter] || [];
    this.filteredNotifications = this.notifications.filter(n =>
      allowedTypes.includes(n.type || NotificationType.System)
    );
  }

  getFilterCount(value: string): number {
    if (value === 'all') return this.notifications.length;

    const typeMap: Record<string, NotificationType[]> = {
      'leave': [NotificationType.LeaveRequest, NotificationType.LeaveApproved, NotificationType.LeaveRejected],
      'meetings': [NotificationType.MeetingMention, NotificationType.Comment],
      'approvals': [NotificationType.Approval],
      'system': [NotificationType.System, NotificationType.Reaction]
    };

    const allowedTypes = typeMap[value] || [];
    return this.notifications.filter(n =>
      allowedTypes.includes(n.type || NotificationType.System)
    ).length;
  }

  getAvatarConfig(note: NotificationVM) {
    return getNotificationAvatar(note.type || NotificationType.System);
  }

  getStatusTag(note: NotificationVM) {
    return getStatusTag(note.type || NotificationType.System);
  }

  highlightMessage(message: string): SafeHtml {
    if (!message) return '';

    let html = this.escapeHtml(message);

    // Bold names at the start
    html = html.replace(/^([A-Z][a-z]+\s+[A-Z][a-z]+)/, '<strong>$1</strong>');
    html = html.replace(/Leave for\s+([A-Z][a-z]+\s+[A-Z][a-z]+)/, 'Leave for <strong>$1</strong>');
    html = html.replace(/from\s+([A-Z][a-z]+\s+[A-Z][a-z]+)/, 'from <strong>$1</strong>');

    // Highlight dates
    html = html.replace(/\(([^)]+\d{4}[^)]*)\)/g, '(<span class="highlight">$1</span>)');
    html = html.replace(
      /from\s+(\w+\s+\d{1,2}(?:\s+\d{4})?)\s+to\s+(\w+\s+\d{1,2}(?:\s+\d{4})?)/,
      'from <span class="highlight">$1</span> to <span class="highlight">$2</span>'
    );

    // Highlight note titles
    html = html.replace(/the note '([^']+)'/g, 'the note \'<span class="highlight">$1</span>\'');

    // Style rejection reason
    html = html.replace(/Reason:\s*(.+)$/, '<em class="notif-reason">Reason: $1</em>');

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  onMarkAsRead(note: NotificationVM, index: number): void {
    this.formsService.updateNotificationBasedOnID(note.id).subscribe(() => {
      const actual = this.notifications.find(n => n.id === note.id);
      if (actual) actual.isRead = true;
      this.applyFilter();
    });
  }

  onNotificationClick(note: NotificationVM): void {
    // Mark as read
    if (!note.isRead) {
      this.formsService.updateNotificationBasedOnID(note.id).subscribe(() => {
        const actual = this.notifications.find(n => n.id === note.id);
        if (actual) actual.isRead = true;
        this.applyFilter();
      });
    }

    if (note.globalRequestId != null) {
      // "Your request was approved/rejected" is a status update for the requester — nothing to
      // action, so it opens their own list. An approval request opens the dialog, where it can be
      // approved or declined outright rather than hunting for the row in the inbox.
      const isRequesterUpdate = (note.message || '').toLowerCase().startsWith('your');
      if (isRequesterUpdate) {
        const urlTree = this.router.createUrlTree(['/global-requests/my-requests']);
        window.open(this.router.serializeUrl(urlTree), '_blank');
        return;
      }

      this.globalRequestDialog.openRequest(note.globalRequestId)
        .pipe(takeUntilDestroyed(this.destroy))
        .subscribe(result => {
          // A decided request is no longer pending, so refresh the list behind the dialog.
          if (result) this.loadNotifications();
        });
      return;
    }

    // A task list opens on its client's Task Lists step, in this tab's organisation.
    const taskList = taskListLink(note);
    if (taskList) {
      openInOrgTab(this.router, taskList.path, taskList.queryParams, null);
      return;
    }

    // Notes open in this tab's organisation: the thread loads under its own company wherever it
    // opens. Approval queues only hold their own organisation's requests, so one from another
    // organisation is offered as "Open in <organisation>" rather than jumped to.
    const other = note.companyId && note.companyId !== this.companiesService.managedCompanyId
      && this.userCompanies.has(note.companyId) ? note.companyId : null;
    if (note.meetingNoteId || note.rootMeetingNoteId) {
      // noteId is the thread (the page selects threads); targetNoteId the reply to scroll to.
      const root = note.rootMeetingNoteId || note.meetingNoteId;
      openInOrgTab(this.router, '/tagged-notes', {
        noteId: root,
        formId: note.submittedFormId,
        ...(note.meetingNoteId && note.meetingNoteId !== root ? { targetNoteId: note.meetingNoteId } : {}),
      }, null);
    } else if (isPendingChangeNotification(note)) {
      this.openQueue(other, 'This change is waiting for approval', '/admin/pending-changes', null);
    } else if (note.leaveRequestId != null) {
      this.openQueue(other, 'This leave request is', '/hr/leave/pending', { requestId: note.leaveRequestId });
    }
  }

  /** An approval queue: this tab's own in a new tab, or another organisation's behind an explicit offer. */
  private openQueue(otherCompanyId: number | null, what: string, path: string, queryParams: Params | null): void {
    if (!otherCompanyId) {
      openInOrgTab(this.router, path, queryParams, null);
      return;
    }
    const org = this.userCompanies.get(otherCompanyId);
    offerOtherOrgTab(this.snackBar, this.router, {
      message: `${what} in ${org || 'another organisation'}.`,
      orgName: org, path, queryParams, companyId: otherCompanyId,
    });
  }

  onMarkAllRead(): void {
    this.formsService.readAllNotifications().subscribe(() => {
      this.notifications.forEach(n => n.isRead = true);
      this.applyFilter();
      this.snackBar.open('All notifications marked as read', 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top'
      });
    });
  }

  onDelete(note: NotificationVM, index: number): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete Notification',
        text: 'Are you sure you want to delete this notification?',
        subText: 'Yes'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deletingIds.add(note.id);
        this.isLoading = true;

        this.formsService.deleteNotificationBasedOnID(note.id).subscribe({
          next: () => {
            setTimeout(() => {
              this.notifications = this.notifications.filter(n => n.id !== note.id);
              this.deletingIds.delete(note.id);
              this.applyFilter();
              this.isLoading = false;

              this.snackBar.open('Notification deleted', 'Close', {
                duration: 3000,
                horizontalPosition: 'center',
                verticalPosition: 'top'
              });
            }, 300);
          },
          error: (error) => {
            this.deletingIds.delete(note.id);
            this.isLoading = false;
            this.snackBar.open(`Error: ${error.message}`, 'Close', {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top'
            });
          }
        });
      }
    });
  }

  onDeleteAll(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete All Notifications',
        text: 'Are you sure you want to delete all notifications? This cannot be undone.',
        subText: 'Delete All'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isLoading = true;
        this.snackBar.open('Deleting all notifications...', '', { duration: 2000 });

        const ids = this.notifications.map(n => n.id);
        this.deleteSequential(ids, 0);
      }
    });
  }

  private deleteSequential(ids: number[], index: number): void {
    if (index >= ids.length) {
      this.notifications = [];
      this.filteredNotifications = [];
      this.isLoading = false;
      this.snackBar.open('All notifications deleted', 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top'
      });
      return;
    }

    this.formsService.deleteNotificationBasedOnID(ids[index]).pipe(
      catchError(() => of(null))
    ).subscribe(() => {
      this.deleteSequential(ids, index + 1);
    });
  }

  onViewLeave(note: NotificationVM): void {
    this.router.navigate(['/hr/leave/pending'], {
      queryParams: { requestId: note.leaveRequestId }
    });
  }

  trackByNotifId(index: number, item: NotificationVM): number {
    return item.id;
  }
}
